import { AttackPattern, PatternState } from '../AttackPattern.js';
import { DESPERATION_DURATION } from '../../../../config/constants.js';

const TAU = Math.PI * 2;

/**
 * The Unmaking — The Unmade King's final, all-out attack.
 *
 * This is deliberately one continuous sequence rather than a one-shot circle
 * repeated until the timer expires. The shrinking safe pocket remains the
 * King's defining rule, but the King now spends his last seconds escalating
 * the pressure around it: rotating rings, aimed/homing volleys, collapse
 * pulses, and a final dense barrage.
 *
 * The player is still surviving, not dealing damage, during this window. Every
 * major beat has a visual state in Game._drawAttackVisuals and the direct
 * collapse hit is pulse-based, so movement, dashes, and graze skill remain
 * meaningful counterplay instead of an unavoidable damage stream.
 */
export class TheUnmaking extends AttackPattern {
  constructor({ config }) {
    super({
      name: 'The Unmaking',
      telegraphDuration: config.telegraphDuration ?? 1.6,
      // The desperation timer owns the true end of the sequence. Keeping the
      // pattern alive for that same window prevents SpawningSystem from
      // restarting a bland intro telegraph midway through the finale.
      activeDuration: config.activeDuration ?? DESPERATION_DURATION,
      minPhase: 0,
    });
    this.config = config;
  }

  onBegin(boss) {
    this._elapsed = 0;
    this._ringTimer = 0;
    this._volleyTimer = 0.55;
    this._pulseTimer = 1.0;
    this._ringAngle = Math.random() * TAU;
    this._pulseCount = 0;
    this._finaleStarted = false;
    this._safeRadius = this.config.startRadius;
    this._lastPhase = -1;
    this._bossOrigin = { x: boss.x, y: boss.y };
  }

  onTelegraphTick() {
    // The opening warning establishes the rule without resolving damage yet:
    // the safe pocket contracts from the arena-wide opening to the first
    // combat radius before the King starts firing.
    const t = 1 - this._timer / this.telegraphDuration;
    this._safeRadius = this._lerp(this.config.startRadius, this.config.openingRadius ?? 170, t);
  }

  onActiveTick(dt, boss, player, pool) {
    this._elapsed += dt;
    const duration = this.activeDuration;
    const progress = Math.min(1, this._elapsed / duration);
    const phase = progress < 0.25 ? 0 : progress < 0.52 ? 1 : progress < 0.78 ? 2 : 3;
    this._safeRadius = this._lerp(this.config.openingRadius ?? 170, this.config.endRadius, progress);

    if (phase !== this._lastPhase) {
      this._lastPhase = phase;
      this._ringAngle = (this._ringAngle + 0.33) % TAU;
    }

    const finalPressure = phase === 3 ? 1 : 0;
    const ringInterval = this._lerp(this.config.ringInterval ?? 0.86, this.config.finalRingInterval ?? 0.52, progress);
    const volleyInterval = this._lerp(this.config.volleyInterval ?? 1.55, this.config.finalVolleyInterval ?? 0.82, progress);

    this._ringTimer -= dt;
    while (this._ringTimer <= 0) {
      this._ringTimer += ringInterval;
      this._emitRing(boss, pool, progress, finalPressure);
    }

    this._volleyTimer -= dt;
    while (this._volleyTimer <= 0) {
      this._volleyTimer += volleyInterval;
      this._emitVolley(boss, player, pool, progress, finalPressure);
    }

    // Collapse pulses are discrete tests of the shrinking pocket. They are
    // announced by the active visual a fraction before resolving and are
    // protected by Player's normal hit mercy/dash rules.
    this._pulseTimer -= dt;
    const pulseInterval = phase >= 3 ? (this.config.finalPulseInterval ?? 0.9) : (this.config.pulseInterval ?? 1.55);
    while (this._pulseTimer <= 0) {
      this._pulseTimer += pulseInterval;
      this._pulseCount++;
      const dist = Math.hypot(player.x - boss.x, player.y - boss.y);
      if (dist > this._safeRadius + player.radius) {
        player.takeDamage(this.config.pulseDamage ?? this.config.damage ?? 36);
      }
    }

    if (!this._finaleStarted && progress >= 0.78) {
      this._finaleStarted = true;
      this._emitRing(boss, pool, 1, 1);
      this._emitVolley(boss, player, pool, 1, 1);
    }
  }

  _emitRing(boss, pool, progress, finalPressure) {
    const count = Math.round(this._lerp(this.config.ringCount ?? 18, this.config.finalRingCount ?? 32, progress));
    const speed = this._lerp(this.config.ringSpeed ?? 78, this.config.finalRingSpeed ?? 132, progress);
    const damage = this.config.ringDamage ?? 12;
    this._ringAngle = (this._ringAngle + (finalPressure ? 0.18 : 0.10)) % TAU;
    for (let i = 0; i < count; i++) {
      const angle = this._ringAngle + (i / count) * TAU;
      pool.fire({
        x: boss.x,
        y: boss.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius: 2.8,
        damage,
        color: finalPressure ? '#ff3b6b' : '#c04ae0',
        lifetime: 5,
      });
    }
  }

  _emitVolley(boss, player, pool, progress, finalPressure) {
    const count = Math.round(this._lerp(this.config.volleyCount ?? 2, this.config.finalVolleyCount ?? 5, progress));
    const speed = this._lerp(this.config.volleySpeed ?? 118, this.config.finalVolleySpeed ?? 170, progress);
    const damage = this.config.volleyDamage ?? 14;
    const spread = finalPressure ? 0.58 : 0.42;
    const aim = Math.atan2(player.y - boss.y, player.x - boss.x);
    for (let i = 0; i < count; i++) {
      const angle = count === 1 ? aim : aim + (i / (count - 1) - 0.5) * spread;
      pool.fire({
        x: boss.x,
        y: boss.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius: 3,
        damage,
        color: '#ff9fba',
        lifetime: 5,
        homing: true,
        turnRate: finalPressure ? 0.78 : 0.55,
        target: player,
      });
    }
  }

  _lerp(a, b, t) {
    return a + (b - a) * t;
  }

  getVisual(boss) {
    if (this.state === PatternState.TELEGRAPH) {
      return { kind: 'safeZone', x: boss.x, y: boss.y, radius: this._safeRadius, imminent: false };
    }
    if (this.state !== PatternState.ACTIVE) return null;
    const progress = Math.min(1, this._elapsed / this.activeDuration);
    const phase = progress < 0.25 ? 0 : progress < 0.52 ? 1 : progress < 0.78 ? 2 : 3;
    return {
      kind: 'unmakingFinale',
      x: boss.x,
      y: boss.y,
      radius: this._safeRadius,
      progress,
      phase,
      pulse: this._pulseTimer <= 0.22,
      final: phase === 3,
    };
  }
}
